import { derived, writable } from 'svelte/store';
import type {
  EditorGroup,
  EditorLink,
  EditorPayload,
  EditorProfile,
  EditorShortLink,
  EditorStats,
  EditorTab,
  EditorTheme,
  ThemeOptionsCatalog,
  ThemePreset,
} from '../types';

const defaultProfile: EditorProfile = {
  display_name: '',
  username: '',
  department: '',
  team: '',
  bio: '',
  avatar_initial: '?',
  social_links: {},
  email_capture_enabled: false,
  email_capture_title: '소식 받기',
  auto_add_posts: true,
};

const defaultTheme: EditorTheme = {
  template: 'clean',
  button_variant: 'solid',
  button_radius_preset: 'rounder',
  button_shadow: 'none',
  bg_color: '#ffffff',
  text_color: '#0f172a',
  title_color: '#0f172a',
  accent_color: '#2563eb',
  button_color: '#111827',
  button_text_color: '#ffffff',
  card_bg_color: '#ffffff',
  card_opacity: 0.88,
  wallpaper_style: 'fill',
  spacing: 'normal',
  social_icon_style: 'filled',
  header_layout: 'classic',
  title_style: 'text',
  title_size: 'small',
  title_font_override: false,
  badge_color: 'blue',
};

export const activeTab = writable<EditorTab>('links');
export const profile = writable<EditorProfile>(defaultProfile);
export const links = writable<EditorLink[]>([]);
export const shortLinks = writable<EditorShortLink[]>([]);
export const groups = writable<EditorGroup[]>([]);
export const theme = writable<EditorTheme>(defaultTheme);
export const presets = writable<ThemePreset[]>([]);

// 허용값 카탈로그(`PageTheme.options_catalog`). 편집기의 선택지는 전부 여기서 나온다 —
// 컴포넌트가 자기 배열을 들면 서버 검증과 갈라지고, 앱과도 갈라진다.
// 기본값이 빈 객체인 것은 의도다. 서버가 안 내려주면 목록이 비어 눈에 띄게 깨지고,
// 로컬 사본이 조용히 대신 그려지는 일이 없다.
export const themeOptions = writable<ThemeOptionsCatalog>({});

// Curated wallpaper image library — derived from active presets that ship a
// background image. Lets users borrow a curated background URL into the Wall
// editor without applying the entire preset (color/typography stays as-is).
export type WallpaperLibraryItem = {
  id: string;
  name: string;
  category: string;
  bgImageUrl: string;
};

export const wallpaperLibrary = derived<typeof presets, WallpaperLibraryItem[]>(
  presets,
  ($presets) => {
    const seen = new Set<string>();
    const items: WallpaperLibraryItem[] = [];
    for (const preset of $presets) {
      const url = (preset.settings as Record<string, unknown>)?.bg_image_url;
      if (typeof url !== 'string' || !url || seen.has(url)) continue;
      seen.add(url);
      items.push({
        id: preset.id,
        name: preset.name,
        category: preset.category,
        bgImageUrl: url,
      });
    }
    return items;
  },
);
export const stats = writable<EditorStats>({ total_views: 0, total_clicks: 0 });
export const themeDirty = writable(false);
export const canUndoTheme = writable(false);
export const canRedoTheme = writable(false);

const THEME_HISTORY_LIMIT = 50;
let themePast: EditorTheme[] = [];
let themeFuture: EditorTheme[] = [];
let suppressThemeHistory = false;

export function initializeEditor(payload: EditorPayload) {
  suppressThemeHistory = true;
  profile.set({ ...defaultProfile, ...payload.profile });
  links.set(sortByPosition(payload.links || []));
  shortLinks.set(payload.short_links || []);
  groups.set(sortByPosition(payload.groups || []));
  theme.set({ ...defaultTheme, ...payload.theme });
  presets.set(payload.presets || []);
  themeOptions.set(payload.theme_options || {});
  stats.set(payload.stats || { total_views: 0, total_clicks: 0 });
  themeDirty.set(false);
  themePast = [];
  themeFuture = [];
  syncThemeHistoryFlags();
  suppressThemeHistory = false;
}

export function updateThemeField<K extends keyof EditorTheme>(key: K, value: EditorTheme[K]) {
  pushThemeHistorySnapshot();
  theme.update((current) => ({ ...current, [key]: value }));
  themeDirty.set(true);
}

export function mergeTheme(nextTheme: Partial<EditorTheme>, markDirty = true) {
  if (!suppressThemeHistory) pushThemeHistorySnapshot();
  theme.update((current) => ({ ...current, ...nextTheme }));
  themeDirty.set(markDirty);
}

export function resetThemeDirty() {
  themeDirty.set(false);
}

// 다른 탭(Settings 의 배지 색, OG 탭의 템플릿)이 page_theme 의 일부만 따로 저장했을 때
// 그 결과를 스토어에 반영한다. 세 가지를 지킨다 —
//   1. 히스토리에 칸을 남기지 않는다(저장은 편집이 아니다).
//   2. Design 탭의 미저장 변경(dirty)은 건드리지 않는다.
//   3. 과거·미래 스냅샷에도 같은 값을 써 넣는다. 안 그러면 Design 에서 Undo/Redo 한 번에
//      방금 저장한 값이 스냅샷 속 옛 값으로 되돌아간다(codex P2, 2026-09-15).
export function applySavedThemeFields(fields: Partial<EditorTheme>) {
  theme.update((current) => ({ ...current, ...fields }));
  themePast = themePast.map((snapshot) => ({ ...snapshot, ...fields }));
  themeFuture = themeFuture.map((snapshot) => ({ ...snapshot, ...fields }));
}

export function undoTheme() {
  if (themePast.length === 0) return;

  suppressThemeHistory = true;
  const current = getThemeSnapshot();
  const previous = themePast.pop();
  if (!previous) {
    suppressThemeHistory = false;
    return;
  }

  themeFuture.unshift(current);
  theme.set(previous);
  themeDirty.set(true);
  syncThemeHistoryFlags();
  suppressThemeHistory = false;
}

export function redoTheme() {
  if (themeFuture.length === 0) return;

  suppressThemeHistory = true;
  const current = getThemeSnapshot();
  const next = themeFuture.shift();
  if (!next) {
    suppressThemeHistory = false;
    return;
  }

  themePast.push(current);
  theme.set(next);
  themeDirty.set(true);
  syncThemeHistoryFlags();
  suppressThemeHistory = false;
}

// position 순으로 정렬된 원본. 미리보기는 여기에 서버 공개 판정을 걸어서 쓴다.
export const orderedLinks = derived(links, ($links) => sortByPosition($links));
export const orderedGroups = derived(groups, ($groups) => sortByPosition($groups));

export const groupedLinks = derived([orderedLinks, orderedGroups], ([$orderedLinks, $orderedGroups]) => {
  const groupMap = new Map($orderedGroups.map((group) => [group.id, group]));
  const grouped = $orderedGroups.map((group) => ({
    ...group,
    links: $orderedLinks.filter((link) => link.link_group_id === group.id),
  }));

  const ungroupedLinks = $orderedLinks.filter((link) => !link.link_group_id || !groupMap.has(link.link_group_id));

  return { grouped, ungroupedLinks };
});

export const previewTheme = derived(theme, ($theme) => {
  const radiusMap: Record<string, string> = {
    square: '0px',
    round: '8px',
    rounder: '16px',
    full: '9999px',
  };
  const buttonRadius = radiusMap[$theme.button_radius_preset || 'rounder'] || '16px';

  return {
    background: $theme.bg_gradient || $theme.bg_color || '#ffffff',
    textColor: $theme.text_color || '#0f172a',
    titleColor: $theme.title_color || $theme.text_color || '#0f172a',
    accentColor: $theme.accent_color || '#2563eb',
    cardBackground: $theme.card_bg_color || '#ffffff',
    cardOpacity: $theme.card_opacity ?? 0.88,
    buttonColor: $theme.button_color || $theme.accent_color || '#111827',
    buttonTextColor: $theme.button_text_color || '#ffffff',
    buttonRadius,
    buttonVariant: $theme.button_variant || 'solid',
    buttonShadow: $theme.button_shadow || 'none',
  };
});

function sortByPosition<T extends { position: number }>(items: T[]): T[] {
  return [...items].sort((left, right) => left.position - right.position);
}

function pushThemeHistorySnapshot() {
  if (suppressThemeHistory) return;

  theme.update((current) => {
    const snapshot = { ...current };
    const last = themePast[themePast.length - 1];
    if (!last || JSON.stringify(last) !== JSON.stringify(snapshot)) {
      themePast.push(snapshot);
      if (themePast.length > THEME_HISTORY_LIMIT) themePast.shift();
      themeFuture = [];
      syncThemeHistoryFlags();
    }
    return current;
  });
}

function getThemeSnapshot() {
  let snapshot = { ...defaultTheme };
  theme.update((current) => {
    snapshot = { ...current };
    return current;
  });
  return snapshot;
}

function syncThemeHistoryFlags() {
  canUndoTheme.set(themePast.length > 0);
  canRedoTheme.set(themeFuture.length > 0);
}
